import { motion, useReducedMotion } from 'framer-motion'
import { Network, UserRound } from 'lucide-react'
import { cn } from '@/lib/utils'

/* Drawn in a 640x360 box; nodes are HTML placed by percentage over it so
   text stays crisp and the lines can stretch to the frame on narrow screens. */
const W = 640
const H = 360
const CY = 180
const COORD_X = 210
const SPEC_X = 420

const PULSE = 0.08

interface Branch {
  d: string
  /** Advice bounces back to the coordinator; a delegated reply runs on. */
  returns: boolean
  delay: number
}

const BRANCHES: Array<Branch> = [
  { d: `M${COORD_X},${CY} C300,${CY} 320,64 ${SPEC_X},64`, returns: true, delay: 0 },
  { d: `M${COORD_X},${CY} C300,${CY} 320,296 ${SPEC_X},296`, returns: true, delay: 1.1 },
  { d: `M${COORD_X},${CY} L592,${CY}`, returns: false, delay: 2.2 },
]

const SPECIALISTS = [
  { label: 'Senior Architect', x: SPEC_X, y: 64 },
  { label: 'Reviewer', x: SPEC_X, y: CY, delegate: true },
  { label: 'Coder', x: SPEC_X, y: 296 },
]

const at = (x: number, y: number) => ({
  left: `${(x / W) * 100}%`,
  top: `${(y / H) * 100}%`,
})

function Endpoint({ x, label }: { x: number; label: string }) {
  return (
    <span
      className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2"
      style={at(x, CY)}
    >
      <span className="h-2 w-2 rounded-full border border-[var(--color-border-strong)] bg-[var(--color-bg-primary)]" />
      <span className="absolute top-full mt-1 font-mono text-[10px] text-[var(--color-text-tertiary)]">
        {label}
      </span>
    </span>
  )
}

interface TeamOrbitDiagramProps {
  label: string
}

/** Coordinator, three specialists, and where each answer goes. */
export function TeamOrbitDiagram({ label }: TeamOrbitDiagramProps) {
  const reduce = useReducedMotion()

  return (
    <div
      role="img"
      aria-label={label}
      className="surface-bevel relative aspect-[4/3] w-full overflow-hidden rounded-[1.75rem] sm:aspect-[16/9]"
    >
      <div aria-hidden className="absolute inset-6 sm:inset-8">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="absolute inset-0 h-full w-full overflow-visible"
        >
          <line
            x1={48}
            y1={CY}
            x2={COORD_X}
            y2={CY}
            vectorEffect="non-scaling-stroke"
            className="stroke-[var(--color-border-strong)]"
          />
          {BRANCHES.map((b) => (
            <g key={b.d}>
              <path
                d={b.d}
                fill="none"
                vectorEffect="non-scaling-stroke"
                className={b.returns ? 'stroke-[var(--color-border-strong)]' : 'stroke-[var(--color-info)]'}
              />
              {!reduce && (
                <motion.path
                  d={b.d}
                  fill="none"
                  pathLength={1}
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  strokeDasharray={`${PULSE} 1`}
                  className={b.returns ? 'stroke-primary' : 'stroke-[var(--color-info)]'}
                  initial={{ strokeDashoffset: PULSE }}
                  animate={{ strokeDashoffset: -1 }}
                  transition={{
                    duration: 1.6,
                    ease: 'easeInOut',
                    delay: b.delay,
                    repeat: Infinity,
                    repeatType: b.returns ? 'reverse' : 'loop',
                    repeatDelay: 1.6,
                  }}
                />
              )}
            </g>
          ))}
        </svg>

        <Endpoint x={48} label="request" />
        <Endpoint x={592} label="reply" />

        <span
          className="surface-bevel absolute flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-xl border-primary/60 p-2.5 sm:px-4 sm:py-3"
          style={at(COORD_X, CY)}
        >
          <Network className="size-3.5 text-primary" strokeWidth={1.75} />
          <span className="eyebrow hidden text-[10px] text-primary sm:inline">Coordinator</span>
        </span>

        {SPECIALISTS.map((s) => (
          <span
            key={s.label}
            className="surface-bevel absolute flex -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 rounded-full px-3 py-1.5 whitespace-nowrap"
            style={at(s.x, s.y)}
          >
            <UserRound
              className={cn(
                'size-3',
                s.delegate ? 'text-[var(--color-info-text)]' : 'text-[var(--color-text-tertiary)]',
              )}
              strokeWidth={1.75}
            />
            <span className="text-caption font-medium text-[var(--color-text-primary)]">
              {s.label}
            </span>
          </span>
        ))}
      </div>
    </div>
  )
}
